import { IComponentOptions, IAugmentedJQuery } from "angular";

import { generateExistingAttachment } from "../helpers";
import { AttachmentTargetType, IUiAttachment } from "../models";
import { AttachmentService } from "../services/attachmentService";
import { IFileUploading } from "@gtmhub/files";
import { IIndicator, UIErrorHandlingService } from "@gtmhub/error-handling";
import { GtmhubController } from "@gtmhub/core";

type DndErrorType = "maxSizeExceeded";

export class AttachmentsCtrl extends GtmhubController {
  targetId: string;
  targetType: AttachmentTargetType;
  dragEnterAreaSelector: string;
  attachments: IUiAttachment[];
  indicators: {
    loading?: IIndicator;
  };
  dnd: {
    dragging: boolean;
    uploadError: {
      fileNames: string;
      type: DndErrorType;
      multiple: boolean;
    };
  };

  static $inject = ["$element", "AttachmentService", "UIErrorHandlingService"];

  constructor(private $element: IAugmentedJQuery, private attachmentService: AttachmentService, private uiErrorHandlingService: UIErrorHandlingService) {
    super();
  }

  $onInit(): void {
    this.indicators = {
      loading: { progress: true },
    };
    this.dnd = {
      dragging: false,
      uploadError: null,
    };

    this.attachmentService.getAttachments({ targetId: this.targetId, targetType: this.targetType }).then(
      (attachments) => {
        delete this.indicators.loading;
        this.attachments = attachments.items.map((attachment) => ({ uploading: null, existing: generateExistingAttachment(attachment) }));
      },
      (rejection) => {
        delete this.indicators.loading;
        this.uiErrorHandlingService.handleModal(rejection);
      }
    );

    const dropBoxEl = this.$element[0].querySelector(".drop-box");
    dropBoxEl.addEventListener("dragenter", this.startDragging);
    dropBoxEl.addEventListener("dragleave", this.onDropZoneDragEnd);
    dropBoxEl.addEventListener("dragend", this.onDropZoneDragEnd);
    dropBoxEl.addEventListener("drop", this.onDropZoneDragEnd);
    this.onDestroy(() => {
      dropBoxEl.removeEventListener("dragenter", this.startDragging);
      dropBoxEl.removeEventListener("dragleave", this.onDropZoneDragEnd);
      dropBoxEl.removeEventListener("dragend", this.onDropZoneDragEnd);
      dropBoxEl.removeEventListener("drop", this.onDropZoneDragEnd);
    });

    if (this.dragEnterAreaSelector) {
      const dragEnterAreaEl = document.querySelector(this.dragEnterAreaSelector);
      if (dragEnterAreaEl) {
        dragEnterAreaEl.addEventListener("dragenter", this.startDragging);
        this.onDestroy(() => dragEnterAreaEl.removeEventListener("dragenter", this.startDragging));
      }
    }
  }

  attach($files: IFileUploading[]): void {
    $files.forEach(($file) => this.addToAttachments($file));
  }

  isAnyAttachmentDisplayed(): boolean {
    return this.attachments && this.attachments.some((attachment) => attachment.existing || attachment.uploading);
  }

  validate($file: IFileUploading): boolean | DndErrorType {
    if (!$file || $file.$error) {
      return false;
    }

    // max allowed size is 25MB
    const maxSize25MBInBytes = 26214400;

    if ($file.size > maxSize25MBInBytes) {
      this.updateDragAndDropError($file);

      return "maxSizeExceeded";
    }

    return true;
  }

  resetIndicator(indicator: keyof AttachmentsCtrl["indicators"]): void {
    delete this.indicators[indicator];
  }

  private updateDragAndDropError = ($file: IFileUploading): void => {
    if (!this.dnd.uploadError) {
      this.dnd.uploadError = {
        fileNames: `"${$file.name}"`,
        type: "maxSizeExceeded",
        multiple: false,
      };
    } else {
      this.dnd.uploadError.multiple = true;
      this.dnd.uploadError.fileNames += `, "${$file.name}"`;
    }
  };

  private addToAttachments = ($file: IFileUploading): void => {
    if ($file && !$file.$error) {
      const attachmentToUpload: IUiAttachment = {
        existing: null,
        uploading: {
          file: $file,
          progress: 0,
          targetId: this.targetId,
          targetType: this.targetType,
        },
      };

      this.attachments.push(attachmentToUpload);
    }
  };

  private onDropZoneDragEnd = (): void => {
    this.dnd.dragging = false;
  };

  private startDragging = (): void => {
    this.dnd.uploadError = null;
    this.dnd.dragging = true;
  };
}

export const AttachmentsComponent: IComponentOptions = {
  templateUrl: require("./attachments.jade"),
  controller: AttachmentsCtrl,
  bindings: {
    targetId: "<",
    disabled: "<",
    targetType: "<",
    dragEnterAreaSelector: "@",
  },
};
